import { Link } from "react-router-dom";
import { Tag, MapPinned } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useT } from "@/lib/i18n";
import { useApi, useCatalog } from "@/lib/data";
import { fileUrl } from "@/lib/api";
import { themeOf } from "@/lib/themes";

export const TownSelect = ({ value, onChange, className = "", testid = "town-select" }) => {
  const { t, tn } = useT();
  const { towns } = useCatalog();
  return (
    <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
      <SelectTrigger className={className} data-testid={testid}><MapPinned className="mr-1 h-4 w-4 opacity-70" /><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all" data-testid={`${testid}-all`}>{t("all_towns")}</SelectItem>
        {towns.map((tw) => <SelectItem key={tw.id} value={tw.id} data-testid={`${testid}-${tw.slug}`}>{tn(tw.name)}</SelectItem>)}
      </SelectContent>
    </Select>
  );
};

export const AdBanner = ({ town }) => {
  const { t } = useT();
  const { data = [] } = useApi(["ads", town], `/ads?town=${town}`);
  if (!data.length) return null;
  return (
    <section className="no-scrollbar mt-6 flex snap-x gap-3 overflow-x-auto px-5 lg:px-8" data-testid="ad-banners">
      {data.map((ad) => {
        const img = (
          <div className="relative h-32 w-[85%] flex-shrink-0 snap-start overflow-hidden rounded-2xl border border-line md:h-44 md:w-[48%]" data-testid={`ad-${ad.id}`}>
            <img src={fileUrl(ad.image_file_id, 800)} alt={ad.title} loading="lazy" decoding="async" className="h-full w-full object-cover" />
            <span className="absolute left-2 top-2 rounded bg-ink/80 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white">{t("ad_label")}</span>
          </div>
        );
        if (!ad.link_url) return <div key={ad.id} className="contents">{img}</div>;
        return ad.link_url.startsWith("/")
          ? <Link key={ad.id} to={ad.link_url} className="contents">{img}</Link>
          : <a key={ad.id} href={ad.link_url} target="_blank" rel="noreferrer sponsored" className="contents">{img}</a>;
      })}
    </section>
  );
};

export const DealCard = ({ d, b }) => {
  const { t, fmtDate, lp } = useT();
  const th = themeOf(b?.theme);
  const body = (
    <article className="flex h-full gap-3 rounded-2xl border border-line bg-white p-3" data-testid={`deal-${d.id}`}>
      {d.photo_file_id ? <img src={fileUrl(d.photo_file_id, 400)} alt={d.title} loading="lazy" decoding="async" className="h-20 w-20 flex-shrink-0 rounded-xl object-cover" />
        : <span className="grid h-20 w-20 flex-shrink-0 place-items-center rounded-xl" style={{ backgroundColor: th.soft, color: th.main }}><Tag className="h-7 w-7" /></span>}
      <div className="min-w-0">
        <p className="line-clamp-2 font-bold leading-tight text-ink">{d.title}</p>
        {b && <p className="mt-0.5 truncate text-xs font-semibold" style={{ color: th.main }}>{b.name}</p>}
        {d.description && <p className="mt-0.5 line-clamp-2 text-xs text-slate-500">{d.description}</p>}
        <p className="mt-1 text-[11px] font-semibold text-slate-400">{t("deal_until", { date: fmtDate(d.valid_until) })}</p>
      </div>
    </article>
  );
  return b?.slug ? <Link to={lp(`/s/${b.slug}`)} className="block w-72 flex-shrink-0 snap-start md:w-auto">{body}</Link> : body;
};

export const DealsStrip = ({ town }) => {
  const { t } = useT();
  const { data = [] } = useApi(["deals", town], `/deals?town=${town}`);
  if (!data.length) return null;
  return (
    <section className="pt-7 lg:pt-10" data-testid="deals-strip">
      <h2 className="px-5 text-base font-bold text-ink md:text-lg lg:px-8">{t("deals_title")}</h2>
      <div className="no-scrollbar mt-3 flex snap-x gap-3 overflow-x-auto px-5 md:grid md:grid-cols-2 md:overflow-visible lg:grid-cols-3 lg:px-8">
        {data.map((d) => <DealCard key={d.id} d={d} b={d.business} />)}
      </div>
    </section>
  );
};
